import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { postOverlay } from "@/lib/overlayApi";
import type { DateRange } from "@/lib/dateRange";

export interface Unavailable {
  panel: string;
  reason: string;
}

export interface ReportResponse<T = Record<string, unknown>> {
  window: { start: string; end: string; as_of: string };
  rows?: T[];
  periods?: T[];
  series?: T[];
  fill_rate?: number | null;
  /**
   * Field names mirror the API EXACTLY. They used to read `ordered` /
   * `delivered` / `lines`, which the server has never sent - it sends
   * `closed_*` - so every one of them resolved to undefined and the card
   * rendered "— of — units, — lines" beside a live percentage.
   */
  fill_rate_basis?: {
    basis?: string;
    closed_units_ordered?: number;
    closed_delivered_quantity?: number;
    closed_lines?: number;
    /**
     * The window these PO figures cover. On the close-date basis this is
     * simply the picker's window: every PO counted has already closed, so
     * there is no settlement lag to shift around.
     */
    window_start?: string;
    window_end?: string;
    /** The equal-length window before the settled one, and its line count. */
    previous_window_start?: string;
    previous_window_end?: string;
    previous_closed_lines?: number;
    /** The span the closed POs actually cover inside the window. */
    first_close?: string | null;
    last_close?: string | null;
  };
  /** Fill rate over the window before the settled one. Null when none settled. */
  fill_rate_previous?: number | null;
  /** The eight ad slots, pooled over the selected window. */
  dayparts?: Array<Record<string, unknown>>;
  /** Same three fields, on the fill-rate table's own response. */
  po_window?: {
    start: string;
    end: string;
    settled_through: string | null;
    shifted: boolean;
  };
  /** The server's own statement of which basis the figures are on. */
  basis_note?: string;
  /**
   * Which grain the sub-line table was grouped on. 'plan' means the client's
   * own plan lines; 'l2_category_name' means the warehouse's categories,
   * served only to a tenant with no sub-line map. Stated rather than assumed —
   * the page called a category a sub-line for weeks because nothing said
   * otherwise.
   */
  subline_basis?: "plan" | "l2_category_name";
  /**
   * Actuals the server could not place against any planned sub-line. Surfaced
   * rather than dropped: rupees that vanish between the total and the table
   * are the kind of gap nobody finds until a client adds up the column.
   */
  unmapped?: { sku_names: string[]; units: number; gmv: number };
  /** Plan vs actual returns a summed row alongside the per-sub-line ones. */
  totals?: T;
  /**
   * Marketing only: the two per-unit figures behind the ROAS-basis warning.
   * Computed by the server each window, never written down — the published
   * report's version of this note was a frozen string that sat in the header
   * quoting three-week-old figures.
   */
  roas_basis?: {
    ad_per_unit: number | null;
    portal_per_unit: number | null;
    ratio: number | null;
    campaigns: number | null;
    direct_sales: number | null;
    direct_qty: number | null;
    portal_units: number | null;
    portal_basis: string;
    note: string;
  };
  /** Which plan version the actuals were measured against. */
  plan_version?: {
    version: number;
    created_at: string;
    created_by: string;
    note: string;
  };
  /** Per-SKU portfolio data for the sub-line → SKU drill-down. */
  portfolio_skus?: Array<{
    sku_name: string;
    product_id: string;
    subline: string;
    gmv_mrp: number;
    units: number;
    osa: number | null;
    asp: number | null;
    discount_pct: number | null;
    est_sales_mrp: number | null;
    market_share: number | null;
    prev_gmv_mrp: number;
    prev_units: number;
    prev_osa: number | null;
    prev_asp: number | null;
    prev_discount_pct: number | null;
    prev_market_share: number | null;
    fill_rate: number | null;
    daily: Array<{ d: string; gmv: number }>;
  }>;
  /** Per-SKU plan from BQ, keyed by product_id. */
  sku_plan?: Record<
    string,
    { units: number; gmv: number; spend: number; roas: number | null }
  >;
  /** Market size for the universe (denominator for market share). */
  market_size?: number | null;
  market_size_prev?: number | null;
  universe_name?: string;
  unavailable?: Unavailable[];
  response_meta: { bytes_scanned: number };
}

/**
 * One hook for every report panel.
 *
 * The panels differ only in path and payload, so a generic reader keeps the
 * pages free of near-identical fetch boilerplate — and means the window,
 * tenant and error handling can only be got right or wrong in one place.
 */
export function useReport<T = Record<string, unknown>>(
  path: string,
  customer: string,
  range: DateRange,
  extra: Record<string, unknown> = {},
  enabled = true,
): UseQueryResult<ReportResponse<T>, Error> {
  return useQuery({
    queryKey: [path, customer, range, extra],
    enabled: enabled && customer !== "",
    staleTime: 10 * 60 * 1000,
    queryFn: ({ signal }) =>
      postOverlay<T>(
        path,
        { customer_name: customer, window: range, ...extra },
        signal,
      ) as unknown as Promise<ReportResponse<T>>,
  });
}
